import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import VariableContext from '../contexts/VariableContext';
import FetchingFreq from '../contexts/FetchingFreq';
import apiService from '../services/apiService';
import analysisTypes, { CATEGORIES } from './DataAnalysisParts/analysisTypes';
import AnalysisParamsForm from './DataAnalysisParts/AnalysisParamsForm';
import AnalysisResults from './DataAnalysisParts/AnalysisResults';
import AnalysisHistory from './DataAnalysisParts/AnalysisHistory';
import { resolveKeysToVarNames } from './DataAnalysisParts/VariablePicker';
import './styles/DataAnalysis.css';

function buildParamsPayload(analysisType, values, dataGroup) {
  const out = {};
  for (const p of analysisType.params) {
    if (p.type === 'select') {
      out[p.name] = values[p.name] ?? p.default;
    } else if (p.type === 'number') {
      const v = values[p.name];
      out[p.name] = v === '' || v === undefined || v === null ? (p.default ?? null) : v;
    } else if (p.multiple) {
      out[p.name] = resolveKeysToVarNames(values[p.name] || [], dataGroup);
    } else {
      const names = resolveKeysToVarNames(values[p.name] ? [values[p.name]] : [], dataGroup);
      out[p.name] = names[0] || null;
    }
  }
  return out;
}

function validateParams(analysisType, values, dataGroup) {
  for (const p of analysisType.params) {
    if (p.type === 'select' || p.type === 'number') continue;
    const raw = values[p.name];
    const resolved = p.multiple
      ? resolveKeysToVarNames(raw || [], dataGroup)
      : resolveKeysToVarNames(raw ? [raw] : [], dataGroup);
    if (p.required && resolved.length === 0) return `"${p.label}" is required.`;
    if (p.min && resolved.length < p.min) return `"${p.label}" needs at least ${p.min} variable(s).`;
  }
  return null;
}

function DataAnalysis() {
  const { dataGroup, projectId } = useContext(VariableContext);
  const { setActivePage } = useContext(FetchingFreq);

  const [selectedTypeId, setSelectedTypeId] = useState(analysisTypes[0].id);
  const [values, setValues] = useState({});
  const [variableTypes, setVariableTypes] = useState(null);
  const [result, setResult] = useState(null);
  const [runLabel, setRunLabel] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setActivePage("analysis"); }, [setActivePage]);

  useEffect(() => {
    if (!projectId) return;
    apiService.get(`/analysis/variable-types/${projectId}`)
      .then(({ data }) => setVariableTypes(data))
      .catch((err) => console.error("Failed to load variable types:", err));
  }, [projectId]);

  const selectedType = useMemo(
    () => analysisTypes.find((a) => a.id === selectedTypeId),
    [selectedTypeId]
  );

  const handleSelectType = (id) => {
    setSelectedTypeId(id);
    setValues({});
    setError(null);
    setResult(null);
  };

  const handleParamChange = (name, val) => {
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const runAnalysis = useCallback(async () => {
    if (!projectId) { setError("No project selected."); return; }
    const validationError = validateParams(selectedType, values, dataGroup || {});
    if (validationError) { setError(validationError); return; }

    setRunning(true);
    setError(null);
    try {
      const params = buildParamsPayload(selectedType, values, dataGroup || {});
      const { data } = await apiService.post('/analysis/run', {
        projectId,
        analysisType: selectedType.id,
        params,
        label: selectedType.label,
      });
      setResult(data);
      setRunLabel(selectedType.label);
      setHistoryRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err.message || "Analysis failed.");
    } finally {
      setRunning(false);
    }
  }, [projectId, selectedType, values, dataGroup]);

  const handleSelectHistoryRun = (run) => {
    setResult({ analysisType: run.analysisType, ...run.result });
    setRunLabel(run.label);
    setSelectedTypeId(run.analysisType);
  };

  return (
    <div className="analysis-workbench">
      <aside className="analysis-sidebar">
        {CATEGORIES.map((cat) => (
          <div className="analysis-category" key={cat}>
            <h5>{cat}</h5>
            {analysisTypes.filter((a) => a.category === cat).map((a) => (
              <button
                key={a.id}
                className={`analysis-type-card ${a.id === selectedTypeId ? 'active' : ''}`}
                onClick={() => handleSelectType(a.id)}
              >
                <span className="analysis-type-title">{a.label}</span>
                <span className="analysis-type-desc">{a.description}</span>
              </button>
            ))}
          </div>
        ))}
        <button className="analysis-history-toggle" onClick={() => setShowHistory((s) => !s)}>
          {showHistory ? 'Hide' : 'Show'} Run History
        </button>
        {showHistory && (
          <AnalysisHistory projectId={projectId} refreshKey={historyRefreshKey} onSelectRun={handleSelectHistoryRun} />
        )}
      </aside>

      <main className="analysis-main">
        <div className="analysis-config-panel">
          <h3>{selectedType.label}</h3>
          <p className="analysis-type-desc-full">{selectedType.description}</p>
          <AnalysisParamsForm
            analysisType={selectedType}
            dataGroup={dataGroup || {}}
            variableTypes={variableTypes}
            values={values}
            onChange={handleParamChange}
          />
          {error && <div className="analysis-error">{error}</div>}
          <button className="analysis-run-btn" onClick={runAnalysis} disabled={running}>
            {running ? 'Running...' : `Run ${selectedType.label}`}
          </button>
        </div>

        <div className="analysis-results-panel">
          {result ? (
            <AnalysisResults analysisType={result.analysisType} analysisLabel={runLabel} result={result} />
          ) : (
            <div className="analysis-empty-state">
              Choose an analysis type on the left, select your variables, and click Run.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default DataAnalysis;
